import useQueryParams from 'hooks/useQueryParams'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveItem } from './PatientSidebar/patientSidebarSlice'
import { PatientSidebarItemKeys } from './PatientSidebar/patientSidebarConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useRolePermissions from 'hooks/useRolePermissions'
import { isUserOfAnyAdminRole } from 'utils/authUtils'
import { resolvePatientViewAction, PatientViewAction, areViewInputsLoaded } from './patientViewRouting'

export default () => {
  const dispatch = useDispatch()
  const { setQueryParams, queryParams } = useQueryParams()
  const { copilotControl: copilotFF, liveCalls: liveCallsFF, greyfinchIntegration: greyfinchFF } = useFeatureFlags()
  const { isGP, isHI, grinPlanKey } = usePlanLimits()
  const { isHiUser } = useRolePermissions()
  const appSettings = useSelector(s => s.profileReducer?.doctor?.user?.appSettings)
  // Used as a "user data loaded" signal — gates the no-requestedView path
  // (where we populate the URL with the saved or default view) so we don't
  // pick a wrong default while isHI / copilotFF are still null on initial
  // render. Note: doctor.id loads BEFORE billing.grinPlan in some sessions,
  // so this alone is NOT enough to safely rewrite an existing patientView
  // value — see the KNOWN_VIEWS check below for that case.
  const doctorId = useSelector(s => s.profileReducer?.doctor?.id)

  const defaultPatientView = useMemo(() => {
    if (isHiUser) {
      return PatientSidebarItemKeys.Chat
    }
    if (isGP) {
      return PatientSidebarItemKeys.Notes
    }
    if (isHI && copilotFF) {
      return PatientSidebarItemKeys.Copilot
    }
    return PatientSidebarItemKeys.Chat
  }, [isHiUser, isGP, isHI, copilotFF])

  const savedView = useMemo(() => {
    try {
      return JSON.parse(appSettings || '{}')?.defaultPatientView
    } catch (e) {
      return undefined
    }
  }, [appSettings])

  const allowedViews = useMemo(() => {
    const views = (() => {
      if (isGP) {
        return ['notes', 'chat']
      }
      if (isHI && copilotFF) {
        return ['copilot', 'chat', 'notes']
      }
      return ['chat', 'notes']
    })()

    // Append feature-flagged views when their flags are enabled
    if (liveCallsFF) {
      views.push('liveCalls')
    }

    if (greyfinchFF) {
      views.push('greyfinch')
    }

    return views
  }, [isGP, isHI, copilotFF, liveCallsFF, greyfinchFF])

  // The inputs that build allowedViews arrive in separate Redux dispatches: the doctor profile (→ feature
  // flags) and billing (→ plan role, grinPlanKey). Only once they've settled is allowedViews final; until
  // then a not-yet-allowed known view might still become allowed, so we wait rather than rewrite. Admin
  // viewers are excluded (see areViewInputsLoaded) — their context practice's flags lag its plan.
  const isAdminViewer = useMemo(() => isUserOfAnyAdminRole(), [])
  const viewInputsLoaded = areViewInputsLoaded({ doctorId, grinPlanKey, isAdminViewer })

  useEffect(() => {
    const allowedSavedView = savedView && allowedViews.includes(savedView) ? savedView : undefined
    const fallbackView = allowedSavedView || defaultPatientView
    const requestedView = queryParams.patientView

    if (requestedView) {
      const action = resolvePatientViewAction({ requestedView, allowedViews, viewInputsLoaded })

      if (action === PatientViewAction.Dispatch) {
        dispatch(setActiveItem(requestedView))
        return
      }
      // Known view whose gating inputs are still loading — wait for allowedViews to catch up.
      if (action === PatientViewAction.Wait) {
        return
      }

      // Unknown view, or a known view gated by a resolved-off flag / a plan the user doesn't have — it can
      // never become valid, so rewrite to the fallback rather than leave the URL disagreeing with the UI.
      setQueryParams({
        ...queryParams,
        patientView: fallbackView
      })
      return
    }

    // No requestedView on the URL — populate it with the saved or default
    // view. This branch DOES need to wait for user data, otherwise we'd
    // pick a wrong default (Chat) for HI users whose default is Copilot.
    if (!doctorId) return

    if (allowedSavedView) {
      setQueryParams({
        ...queryParams,
        patientView: allowedSavedView
      })
      return
    }

    setQueryParams({
      ...queryParams,
      patientView: defaultPatientView
    })
  }, [dispatch, queryParams, setQueryParams, defaultPatientView, savedView, allowedViews, doctorId, viewInputsLoaded])
}
